import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';

import Svg from '@svg/index';
import { Flex } from '@ui-library/flex';
import { Tooltip } from '@ui-library/tooltip';
import { InputField } from '@ui-library/input';
import { ModalStep } from '@components/common/multiStepModal';
import { ContextMenuV2 } from '@components/common/contextMenu';
import { useMultiStepModalStore } from '@components/common/multiStepModal/modalStore';
import { canCreateDestinationAccount } from '@hooks/useActionGuard/utils/permissionChecks';
import { LargeText } from '@ui-library/typography';

import { useAccountManagementStore } from '../../store';
import { useDeleteAccountPermissions } from './useDeleteAccountPermissions';
import { AccountDetails } from '../accountDetails';
import { SelectableCard } from '../../styles';
import { CurrentAccountBadge } from '../currentAccountBadge';
import { getMetadataSearchString } from '../utils';

interface AccountPickerProps {
  initialAccountId?: string;
  selectedAccountId?: string;
}

export const AccountPickerModalStep: React.FC<AccountPickerProps> = observer(
  ({ initialAccountId, selectedAccountId }) => {
    const accountManagementStore = useAccountManagementStore();
    const multiStepModalStore = useMultiStepModalStore();
    const {
      resourceType,
      compatibleAccounts,
      compatibleAccountDefinitions,
      setAccountId,
      setDeleteAccountId,
    } = accountManagementStore;

    const [newAccountId, setNewAccountId] = useState<string>(
      selectedAccountId ?? initialAccountId ?? '',
    );
    const [searchText, setSearchText] = useState<string>('');
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const sortedCompatibleAccounts = React.useMemo(() => {
      const [current, others] = partition(compatibleAccounts, (a) => a.id === initialAccountId);
      const sortedOthers = orderBy(others, ['updatedAt'], ['desc']);
      return current.length > 0 ? [...current, ...sortedOthers] : sortedOthers;
    }, [compatibleAccounts, initialAccountId]);

    const filteredCompatibleAccounts = React.useMemo(() => {
      if (!searchText) {
        return sortedCompatibleAccounts;
      }

      return sortedCompatibleAccounts.filter((account) => {
        const accountDefinition = compatibleAccountDefinitions.find(
          (def) => def.name === account.accountDefinitionName,
        );

        const nameMatch = account.name.toLowerCase().includes(searchText.toLowerCase());
        const authTypeMatch = accountDefinition?.authenticationType
          ?.toLowerCase()
          .includes(searchText.toLowerCase());
        const metadataString = getMetadataSearchString(account.metadata);
        const metadataMatch = metadataString.toLowerCase().includes(searchText.toLowerCase());

        return nameMatch || authTypeMatch || metadataMatch;
      });
    }, [sortedCompatibleAccounts, searchText, compatibleAccountDefinitions]);

    const { deletePermissionsMap } = useDeleteAccountPermissions(
      filteredCompatibleAccounts,
      resourceType,
    );

    const handleDeleteClick = (accountId: string) => {
      setDeleteAccountId(accountId);
      multiStepModalStore.navigate('deleteAccountConfirmation');
    };

    const createDestinationAccountPermission = canCreateDestinationAccount();

    const noSearchResults = searchText.trim() !== '' && filteredCompatibleAccounts.length === 0;

    return (
      <ModalStep
        gap="16px"
        disabled={newAccountId === '' || newAccountId === initialAccountId}
        header={{
          title: 'Choose from the list of existing accounts',
          description: 'Current denotes the account you are currently using',
        }}
        footer={{
          backButton: {
            visible: true,
            text: 'Add account',
            type: 'secondary',
            size: 'middle',
            icon: <FontAwesomeIcon icon={regular('plus')} />,
            onClick: () => multiStepModalStore.navigate('accountTypePicker'),
            disabled: !createDestinationAccountPermission.isAllowed,
            tooltip: { title: createDestinationAccountPermission.denialReason },
          },
          actionButton: {
            visible: true,
            text: 'Use account',
            onClick: () => setAccountId(newAccountId),
          },
        }}
      >
        <InputField
          placeholder="Search by name, metadata etc"
          value={searchText}
          prefix={<FontAwesomeIcon icon={regular('search')} size="sm" />}
          onChange={(e) => setSearchText(e.target.value)}
          className="m-b-md"
        />
        <Flex
          ref={scrollContainerRef}
          flexGrow
          flexDirection="column"
          gap=".5rem"
          style={{ overflowY: 'auto', height: '50vh' }}
        >
          {noSearchResults ? (
            <Flex flexDirection="column" flexGrow centered>
              <Svg name="empty_box" size={200} />
              <LargeText>No accounts found</LargeText>
            </Flex>
          ) : (
            filteredCompatibleAccounts.map((account) => (
              <SelectableCard
                key={account.id}
                isSelected={newAccountId === account.id}
                onClick={() => setNewAccountId(account.id)}
              >
                <Flex flexGrow flexDirection="column" justifyContent="space-between" gap="16px">
                  <Flex flexDirection="row" justifyContent="space-between" alignItemsCenter>
                    <AccountDetails store={accountManagementStore} accountId={account.id} />
                    {initialAccountId === account.id ? (
                      <CurrentAccountBadge />
                    ) : (
                      <ContextMenuV2
                        scrollContainerRef={scrollContainerRef}
                        items={[
                          {
                            key: 'delete-account',
                            label: (
                              <Tooltip title={deletePermissionsMap[account.id].denialReason}>
                                Delete account
                              </Tooltip>
                            ),
                            danger: true,
                            icon: <FontAwesomeIcon icon={regular('trash')} />,
                            onClick: () => handleDeleteClick(account.id),
                            disabled: !deletePermissionsMap[account.id].isAllowed,
                          },
                        ]}
                        size="small"
                      />
                    )}
                  </Flex>
                </Flex>
              </SelectableCard>
            ))
          )}
        </Flex>
      </ModalStep>
    );
  },
);
